import { useState } from "react";
import { DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import {
  AdminRegionLocationResDto,
  AdminRegionResDto,
} from "service/api/estabrookSchemas";

import useRegionManagementStore, {
  MoveValues,
} from "../state/region-management-store";
import { MoveOrdersLocationIds } from "../state/route-planner-store";
import { useAdminRegionV2ControllerTransferLocationsToRegion } from "service";
import { toast } from "react-toastify";
import {
  findContainer,
  getCurrentRegionsAndItems,
  removeSelectedAddressesOnDrag,
  updateLocationsCache,
  updateRegionsCache,
  updateRegionsOptimistically,
} from "../shared/utils/region-dnd";

export interface RegionManagementDragStateProps {
  sourceRegionId?: string;
}

export const useRegionManagementDragState = ({
  sourceRegionId,
}: RegionManagementDragStateProps) => {
  const [regionsBeforeDrag, setRegionsBeforeDrag] = useState<
    AdminRegionResDto[]
  >([]);
  const [shouldFilterSelected, setShouldFilterSelected] =
    useState<boolean>(true);
  const {
    regions,
    selectedAddresses,
    flatRate,
    onDemand,
    unassignedOrders,
    resetAllSelected,
    setMoveValues,
    setRegions,
  } = useRegionManagementStore();

  const { mutate: moveLocations, isLoading } =
    useAdminRegionV2ControllerTransferLocationsToRegion({
      onSuccess: (data) => {
        toast.success(
          "Locations Moved Successfully. Future Routes have been modified to the new Region Template. Live Route are unaffected.",
        );

        updateRegionsCache(data);
        updateLocationsCache(data, flatRate, onDemand, unassignedOrders);

        resetAllSelected();
      },
    });

  const filterRegions = (
    currentRegions: AdminRegionResDto[],
    currentMovingItem: AdminRegionLocationResDto,
  ): AdminRegionResDto[] => {
    if (selectedAddresses.length > 1) {
      return removeSelectedAddressesOnDrag(
        currentRegions,
        currentMovingItem,
        selectedAddresses,
        shouldFilterSelected,
        setShouldFilterSelected,
      );
    }
    return currentRegions;
  };

  const moveOrderOnDragOver = ({
    active,
    over,
  }: DragOverEvent): AdminRegionResDto[] | undefined => {
    const activeItemId = active.id;
    const overItemId = over?.id;

    // Find the containers
    const activeContainer = findContainer(activeItemId, regions);
    const overContainer = findContainer(overItemId || "", regions);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    const {
      currentRegions,
      currentItem,
      activeIndex,
      overIndex,
      activeRegionItems,
    } = getCurrentRegionsAndItems(
      activeContainer,
      overContainer,
      activeItemId,
      regions,
    );

    if (regionsBeforeDrag.length === 0) {
      setRegionsBeforeDrag(regions);
      setShouldFilterSelected(true);
    }

    if (currentItem === undefined) return;

    const itemToMove: AdminRegionLocationResDto = {
      ...currentItem,
      locationSequence: overIndex as number,
    };

    const filteredRegions = filterRegions(currentRegions, itemToMove);

    if (activeIndex === undefined || !activeRegionItems) return;

    return filteredRegions.map((region) => {
      if (region.id === activeContainer) {
        // If dragging over the same region
        if (activeContainer === overContainer) {
          const updatedLocations = region.locations.reduce(
            (result, location) => {
              if (location.locationId === activeItemId) {
                result.itemToMoveToEnd = {
                  ...location,
                  locationSequence: activeRegionItems.length - 1,
                };
              } else {
                result.locations.push(location);
              }
              return result;
            },
            {
              locations: [] as AdminRegionLocationResDto[],
              itemToMoveToEnd: null as AdminRegionLocationResDto | null,
            },
          );

          if (updatedLocations.itemToMoveToEnd) {
            updatedLocations.locations.push(updatedLocations.itemToMoveToEnd);
          }

          return {
            ...region,
            locations: updatedLocations.locations,
          };
        } else {
          // If dragging to a different region, remove the item
          return {
            ...region,
            locations: region.locations.filter(
              (location) => location.locationId !== activeItemId,
            ),
          };
        }
      }

      // If dragging to a different route, add the item with updated status
      if (region.id === overContainer) {
        return {
          ...region,
          locations: [...region.locations, itemToMove],
        };
      }

      return region;
    });
  };

  const calculateTargetSequence = (
    targetRegionId: string,
    currentItem: AdminRegionLocationResDto | undefined,
    prevItem: AdminRegionLocationResDto | undefined,
  ) => {
    const prevTargetSequence = prevItem?.locationSequence ?? 1;

    // Check if this is an end drop (currentItem and prevItem are the same)
    if (currentItem && currentItem.locationId === prevItem?.locationId) {
      // This is triggered when we drag an item at the end of the drop zone
      // We need to find the max sequence in the TARGET region, not source region
      const targetRegion = regions.find(
        (region) => region.id === targetRegionId,
      );
      if (targetRegion && targetRegion.locations.length > 0) {
        const targetRegionSequences = targetRegion.locations
          .map((location) => location.locationSequence ?? 0)
          .filter((seq) => seq > 0); // Only consider valid sequences

        if (targetRegionSequences.length > 0) {
          const maxSeqNumber = Math.max(...targetRegionSequences);
          return {
            targetSeqNumber: maxSeqNumber + 1,
            visibleTargetSequence: maxSeqNumber + 1, // Show the actual end position
          };
        }
      }
      // If target region has no items, start at sequence 1
      return { targetSeqNumber: 1, visibleTargetSequence: 1 };
    }

    return {
      targetSeqNumber: prevTargetSequence,
      visibleTargetSequence:
        prevTargetSequence === 1 ? prevTargetSequence : prevTargetSequence - 1,
    };
  };

  const handleMoveRegionAddress = async (
    prevOrderId: string,
    currentItem: AdminRegionLocationResDto | undefined,
    activeRegionId: string,
    targetRegionId: string,
  ) => {
    const prevItem =
      prevOrderId === "END_OF_REGION"
        ? null
        : regions
            .flatMap((region) => region.locations)
            .find((location) => location.locationId === prevOrderId);

    if (currentItem) {
      if (!sourceRegionId) {
        toast.error("Please select a region before moving addresses.");
        return;
      }

      // Determine the actual target region ID
      // If targetRegionId is empty or undefined, default to activeRegionId (same region move)
      const actualTargetRegionId = targetRegionId || activeRegionId;

      const sortedSelectedAddresses = selectedAddresses.sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      );

      // Handle sequence calculation for end drops
      let targetSeqNumber: number;
      if (!prevItem) {
        // Dropping at the end - find the max sequence in target region and add 1
        const targetRegion = regions.find(
          (region) => region.id === actualTargetRegionId,
        );
        if (targetRegion && targetRegion.locations.length > 0) {
          const targetRegionSequences = targetRegion.locations
            .map((location) => location.locationSequence ?? 0)
            .filter((seq) => seq > 0);

          targetSeqNumber =
            targetRegionSequences.length > 0
              ? Math.max(...targetRegionSequences) + 1
              : 1;
        } else {
          targetSeqNumber = 1;
        }
      } else {
        const result = calculateTargetSequence(
          actualTargetRegionId,
          currentItem,
          prevItem,
        );
        targetSeqNumber = result.targetSeqNumber;
      }
      let futureTargetSeqNumber = targetSeqNumber;

      const locationIds: MoveOrdersLocationIds[] = sortedSelectedAddresses.map(
        (address) => ({
          locationId: address.id,
          seqNumber: futureTargetSeqNumber++,
        }),
      );

      // Determine if we're dropping at the end of the region
      const targetRegion = regions.find(
        (region) => region.id === actualTargetRegionId,
      );
      const targetRegionLength = targetRegion?.locations?.length || 0;

      // Simplified end drop detection - if targetSeqNumber is at or beyond the region length, it's an end drop
      const isDroppingAtEnd = targetSeqNumber >= targetRegionLength;

      const moveValues: MoveValues = {
        region: actualTargetRegionId,
        sequence:
          isDroppingAtEnd && targetRegionLength > 0
            ? targetRegionLength // Use last existing position for "after"
            : targetSeqNumber,
        order: isDroppingAtEnd ? "after" : "before",
      };

      setMoveValues(moveValues);

      const locationsToMove = locationIds.length
        ? locationIds.map((item) => item.locationId)
        : [currentItem.locationId];

      // Optimistically update regions to reflect the final position
      const updatedRegions = updateRegionsOptimistically(
        regions,
        locationsToMove,
        activeRegionId,
        actualTargetRegionId,
        targetSeqNumber,
      );

      // Update the regions state immediately
      setRegions(updatedRegions);

      // Make the API call to persist the changes

      moveLocations({
        pathParams: {
          id: sourceRegionId,
        },
        body: {
          locations: locationsToMove,
          targetSeqNumber: moveValues.sequence || 0,
          targetRegionId: moveValues?.region || "",
          transferMode: moveValues.order as "before" | "after" | "append",
        },
      });
    }
    return;
  };

  const changeCustomerOrderStatus = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeItemId = active.id;
    const activeContainer = findContainer(activeItemId, regions);
    const overContainer = findContainer(over?.id || "", regions);
    const { currentItem } = getCurrentRegionsAndItems(
      activeContainer ?? "",
      "",
      activeItemId,
      regions,
    );

    if (!activeContainer) {
      return;
    }

    // Determine the target reference item for the drop
    let targetItemId = over?.data.current?.id || active?.data.current?.id;

    // If we're dropping on a region container (not a specific item), use the last item in the target region
    if (
      over?.data.current?.type === "container" &&
      over?.data.current?.regionId
    ) {
      const targetRegion = regions.find(
        (r) => r.id === over.data.current?.regionId,
      );
      if (targetRegion && targetRegion.locations.length > 0) {
        // Use the last item in the target region as the reference point
        const lastItem =
          targetRegion.locations[targetRegion.locations.length - 1];
        targetItemId = lastItem.locationId;
      } else {
        // Empty region - use a special marker or handle differently
        targetItemId = "END_OF_REGION";
      }
    }

    handleMoveRegionAddress(
      targetItemId,
      currentItem,
      activeContainer,
      overContainer || over?.data.current?.regionId || "",
    );
  };

  return {
    moveOrderOnDragOver,
    changeCustomerOrderStatus,
    isUpdating: isLoading,
    regionsBeforeDrag,
    resetRegionsBeforeDrag: () => setRegionsBeforeDrag([]),
  };
};
